import * as React from "react";

import "./tab-guard.scss";

import {cx} from "emotion";

import {Types} from "@Common/types";
import {checkBrowserVersion} from "@Common/ui-components/debug/browser-check";

import {ModalLayout} from "@Xpresso/routes/common/modal-layout/modal-layout";

const LOCAL_STORAGE_KEY = "XDQA_DISABLE_TAB_PROTEC";
const MSG_CHANGED       = (
    "The session has changed. This is typically caused by working on multiple orders across browser tabs. We recommend "
    + "you close this tab."
);
const MSG_ERROR         = (
    "There seems to be a problem with your internet connection. We will keep trying to reconnect."
);
const MSG_EXPIRED       = (
    "This session has expired. This is typically caused by idle timeout or working on multiple accounts across browser "
    + "tabs. We recommend you close this tab. Reloading this tab may affect other active Xpressdocs tabs."
);
const MSG_BUSY          = "Validating session...";

const STATUS_AS_MSG:{[s in Status]: string} = {
    changed: MSG_CHANGED,
    error: MSG_ERROR,
    expired: MSG_EXPIRED,
    match: "All good!",
    validating: MSG_BUSY,
};

const TRUST_TIMEOUT_MS = 100;

type ApiRequest     = Types.Apis.Payloads.Next.Session.ValidateActiveEntity.Request;
type ApiResponse    = Types.Apis.Payloads.Next.Session.ValidateActiveEntity.Response;
type State          = {
    blocking: boolean,
    focusOwners: string[],
    status: Status,
    timestamp: number,
    visible: boolean,
};
type Status         = "validating" | "error" | "match" | "changed" | "expired";
type Action         = (
    | {type: "blur", owner: string}
    | {type: "doneValidating", result: ApiResponse}
    | {type: "focus", owner: string}
    | {type: "errorValidating"}
    | {type: "reset"}
    | {type: "show"}
);
type AttentionListener = (owner:string) => void;

export const TabGuard = ({
    idAgent=null,
    idBranch=null,
    idCompany,
    tabListenersRef=null,
    userLevel,
    validateActiveEntity,
}:{
    idAgent                 ?: string,
    idBranch                ?: string,
    idCompany                : string,
    internalUser            ?: boolean,
    tabListenersRef         ?: (listeners:{onTabBlur: AttentionListener, onTabFocus: AttentionListener}) => void,
    userLevel                : number,
    validateActiveEntity     : (i:ApiRequest, s:AbortSignal) => Promise<ApiResponse>,
}) =>
{
    const [{blocking, status, visible}, emitStateAction] = React.useReducer<State, Action>(reduceState, {
        blocking: false,
        focusOwners: ["root"],
        status: "match",
        timestamp: Date.now(),
        visible: false,
    });

    const onTabBlur = React.useCallback((owner:string) => emitStateAction({type: "blur", owner}), []);
    const onTabFocus = React.useCallback((owner:string) => emitStateAction({type: "focus", owner}), []);
    tabListenersRef({onTabBlur, onTabFocus});

    const dialogRef = React.useRef<HTMLDialogElement>(null);
    React.useEffect(() => dialogRef.current?.[blocking ? "showModal" : "close"](), [blocking]);

    React.useEffect(() =>
    {
        const onWindowBlur = () => onTabBlur("root");
        const onWindowFocus = () => onTabFocus("root");
        window.addEventListener("blur", onWindowBlur)
        window.addEventListener("focus", onWindowFocus);
        return () =>
        {
            window.removeEventListener("blur", onWindowBlur);
            window.removeEventListener("focus", onWindowFocus);
        };
    }, []);

    React.useEffect(() =>
    {
        if (status === "validating") {
            const showTimeout = setTimeout(() => emitStateAction({type: "show"}), 1000);

            let attempt = 1;
            const abortController = new AbortController();
            const retryValidation = () =>
            {
                if (attempt > 3) {
                    emitStateAction({type: "errorValidating"});
                } else if (!abortController.signal.aborted) {
                    validateActiveEntity({idCompany, idBranch, idAgent, userLevel}, abortController.signal).then(
                        (result) => emitStateAction({type: "doneValidating", result})
                    ).catch((error) =>
                    {
                        console.error(error);
                        attempt++;
                        retryValidation();
                    });
                }
            };
            retryValidation();
            return () =>
            {
                clearTimeout(showTimeout);
                abortController.abort();
            };
        }
    }, [status]);

    React.useEffect(() => emitStateAction({type: "reset"}), [idAgent, idBranch, idCompany]);

    return (
        checkBrowserVersion("Safari", 16) ? null :  // Safari prior to 15.4 has a bug that prevents the dialog from showing
        <dialog ref={dialogRef} className={cx("tab-guard-0a127ee51b", {visible})}>
            <ModalLayout
                content={<div className="tab-guard-content-0a127ee51b">{STATUS_AS_MSG[status]}</div>}
            />
        </dialog>
    );
};

export const isTabGuardEnabled = (internalUser=false) => (
    !internalUser || !localStorage[LOCAL_STORAGE_KEY] || ["0", "false"].includes(localStorage[LOCAL_STORAGE_KEY])
);

const reduceState = (state:State, action:Action): State =>
{
    switch (action.type) {
        case "blur": return {
            ...state,
            focusOwners: state.focusOwners.filter(owner => owner != action.owner),
            timestamp: Date.now(),
        };

        case "doneValidating": return (
            state.status === "validating"
            ? {
                status: action.result.code,
                blocking: false, //action.result.code !== "match",
                focusOwners: state.focusOwners,
                timestamp: Date.now(),
                visible: false, //action.result.code !== "match"
            }
            : state
        );

        case "errorValidating": return (
            state.status === "validating"
            ? {
                status: "error",
                blocking: false, //true,
                focusOwners: state.focusOwners,
                timestamp: Date.now(),
                visible: false, //true
            }
            : state
        );

        case "reset": return {
            blocking: false,
            focusOwners: ["root"],
            status: "match",
            timestamp: Date.now(),
            visible: false,
        };

        case "show": return (
            state.status === "match"
            ? state
            : {...state, visible: true}
        );

        case "focus": {
            const newFocusOwners = [...state.focusOwners, action.owner];
            return (
                (
                    state.focusOwners.length === 0
                    && (Date.now() - state.timestamp) > TRUST_TIMEOUT_MS
                )
                ? {
                    blocking: false, //true,
                    focusOwners: newFocusOwners,
                    status: "validating",
                    timestamp: Date.now(),
                    visible: state.visible,
                }
                : {...state, focusOwners: newFocusOwners, timestamp: Date.now()}
            );
        }
    }
};

